import React, { useRef } from "react";
import SignatureCanvas from "react-signature-canvas";

interface SignatureBoxProps {
  label?: string;
  onSave?: (signatureData: string) => void;
  isReadOnly?: boolean;
  value?: string;
}

const SignatureBox: React.FC<SignatureBoxProps> = ({
  label = "Client Signature",
  onSave,
  isReadOnly = false,
  value,
}) => {
  const sigCanvas = useRef<SignatureCanvas>(null);
  const [isEmpty, setIsEmpty] = React.useState(!value);

  React.useEffect(() => {
    if (!value && sigCanvas.current) {
      sigCanvas.current.clear();
      setIsEmpty(true);
    } else if (value) {
      setIsEmpty(false);
    }
  }, [value]);

  const clear = () => {
    sigCanvas.current?.clear();
    setIsEmpty(true);
    if (onSave) onSave("");
  };

  const handleBegin = () => {
    setIsEmpty(false);
  };

  const save = () => {
    if (sigCanvas.current) {
      if (sigCanvas.current.isEmpty()) {
        setIsEmpty(true);
        if (onSave) onSave("");
      } else {
        const data = sigCanvas.current.toDataURL("image/png");
        setIsEmpty(false);
        if (onSave) onSave(data);
      }
    }
  };

  if (isReadOnly) {
    return (
      <div className="w-full space-y-3">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
          {label}
        </h2>
        <div className="w-full h-40 flex items-center justify-center bg-gray-50 border border-gray-100 rounded-2xl overflow-hidden relative group">
          {value ? (
            <img src={value} alt="Signature" className="max-h-full transition-transform group-hover:scale-105" />
          ) : (
            <div className="flex flex-col items-center gap-2 text-gray-400">
              <span className="text-sm italic">No signature provided</span>
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-1 bg-purple-100 opacity-50"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full space-y-3">
      <div className="flex justify-between items-end">
        <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">
          {label}
        </h2>
        {!isEmpty && (
          <button
            type="button"
            onClick={clear}
            className="text-[11px] font-black text-red-500 hover:text-red-700 uppercase tracking-widest transition-colors flex items-center gap-1.5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
            Clear Signature
          </button>
        )}
      </div>

      <div className="w-full h-48 bg-gray-50 border-2 border-dashed border-gray-200 rounded-3xl overflow-hidden touch-none relative transition-all hover:border-purple-200 hover:bg-white group cursor-crosshair">
        {isEmpty && (
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none transition-opacity group-hover:opacity-70">
            <div className="w-12 h-12 bg-purple-50 rounded-2xl flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-purple-400">
                <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
              </svg>
            </div>
            <p className="text-sm font-black text-gray-400 uppercase tracking-widest">Click or Draw to Sign</p>
          </div>
        )}

        <SignatureCanvas
          ref={sigCanvas}
          penColor="#111827"
          velocityFilterWeight={0.1}
          onBegin={handleBegin}
          canvasProps={{
            className: "signature-canvas w-full h-full",
            style: { width: '100%', height: '100%' }
          }}
          onEnd={save}
        />

        {/* Decorative corner */}
        <div className="absolute bottom-4 right-4 text-gray-200 pointer-events-none">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M21 21H3c-1.1 0-2-.9-2-2V5c0-1.1.9-2 2-2h18c1.1 0 2 .9 2 2v14c0 1.1-.9 2-2 2zM3 5v14h18V5H3zm15 12h-2v-2h2v2zm-4 0h-2v-2h2v2zm-4 0H8v-2h2v2zm-4 0H4v-2h2v2z" />
          </svg>
        </div>
      </div>

      <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] text-center">
        Authentication via digital signature
      </p>
    </div>
  );
};

export default SignatureBox;
